import React, { useState, useEffect, useCallback } from "react";
import { useHttpClient } from "shared/hooks/http-hook";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Button from "shared/components/FormElements/Button";
import AreYouSureModal from "components/shared/AreYouSureModal";
import DraftEditor from "./DraftEditor";
import OutreachTimeline from "./OutreachTimeline";

const SCORE_COLOR = (score) => {
  if (score >= 70) return "#2e7d32";
  if (score >= 40) return "#ed6c02";
  return "#d32f2f";
};

const STATUS_OPTIONS = ["NEW", "CRAWLED", "CRAWL_FAILED", "SCORED", "APPROVED", "OUTREACH", "CONTACTED", "REPLIED", "SOLD", "REJECTED", "AUTO_REJECTED", "BOUNCED"];
const CATEGORY_OPTIONS = [
  "YELLOW_BUS_CONTRACTOR",
  "SPED_TRANSPORT",
  "CHARTER_PLUS_SCHOOL",
  "TOO_BIG",
  "NOT_SCHOOL",
  "SCHOOL_TRANSPORT",
  "OTHER",
];

const sectionStyle = {
  padding: "16px",
  borderRadius: "8px",
  backgroundColor: "#fafafa",
  marginBottom: "16px",
  border: "1px solid #eee",
};

const LeadDetail = ({ leadId, onBack }) => {
  const { sendRequest } = useHttpClient();
  const [lead, setLead] = useState(null);
  const [outreach, setOutreach] = useState(null);
  const [notes, setNotes] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [phoneInput, setPhoneInput] = useState("");
  const [category, setCategory] = useState("OTHER");
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [approving, setApproving] = useState(false);

  const fetchLead = useCallback(async () => {
    try {
      const data = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/admin/lead-hunter/leads/${leadId}`);
      setLead(data.lead);
      setOutreach(data.outreach);
      setNotes(data.lead.notes || "");
      setCompanyName(data.lead.companyName || "");
      setEmailInput((data.lead.crawlData?.emails || []).join(", "));
      setPhoneInput(data.lead.phone || (data.lead.crawlData?.phones || []).join(", ") || "");
      setCategory(data.lead.scoring?.category || "OTHER");
    } catch (err) {
      console.error("Failed to fetch lead:", err);
    }
  }, [sendRequest, leadId]);

  useEffect(() => {
    fetchLead();
  }, [fetchLead]);

  const handleSave = useCallback(async () => {
    try {
      const emails = emailInput
        .split(/[,;\s]+/)
        .map((e) => e.trim())
        .filter(Boolean);
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/admin/lead-hunter/leads/${leadId}`,
        "PATCH",
        JSON.stringify({ notes, companyName, category, phone: phoneInput, "crawlData.emails": emails }),
      );
      window.toast.success("Saved");
      fetchLead();
    } catch (err) {
      window.toast.error("Save failed");
    }
  }, [sendRequest, leadId, notes, companyName, category, emailInput, phoneInput, fetchLead]);

  const handleStatusChange = useCallback(async (newStatus) => {
    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/admin/lead-hunter/leads/${leadId}`,
        "PATCH",
        JSON.stringify({ status: newStatus }),
      );
      fetchLead();
      window.toast.success("Status updated");
    } catch (err) {
      window.toast.error("Status update failed");
    }
  }, [sendRequest, leadId, fetchLead]);

  const handleApprove = useCallback(async () => {
    setApproving(true);
    try {
      await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/admin/lead-hunter/leads/${leadId}/approve`, "POST");
      window.toast.success("Lead approved, draft generated");
      fetchLead();
    } catch (err) {
      window.toast.error("Approval failed: " + (err.message || ""));
    }
    setApproving(false);
    setShowApproveModal(false);
  }, [sendRequest, leadId, fetchLead]);

  const handleReject = useCallback(async () => {
    try {
      await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/admin/lead-hunter/leads/${leadId}/reject`, "POST");
      window.toast.success("Lead rejected");
      fetchLead();
    } catch (err) {
      window.toast.error("Reject failed");
    }
    setShowRejectModal(false);
  }, [sendRequest, leadId, fetchLead]);

  const handleRecrawl = useCallback(async () => {
    try {
      window.toast.success("Recrawling...");
      await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/admin/lead-hunter/leads/${leadId}/recrawl`, "POST");
      window.toast.success("Recrawl complete");
      fetchLead();
    } catch (err) {
      window.toast.error("Recrawl failed");
    }
  }, [sendRequest, leadId, fetchLead]);

  const handleMarkSold = useCallback(async () => {
    try {
      await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/admin/lead-hunter/leads/${leadId}/mark-sold`, "POST");
      window.toast.success("Marked as sold");
      fetchLead();
    } catch (err) {
      window.toast.error("Failed");
    }
  }, [sendRequest, leadId, fetchLead]);

  const handleMarkReplied = useCallback(async () => {
    try {
      await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/admin/lead-hunter/leads/${leadId}/mark-replied`, "POST");
      window.toast.success("Marked as replied");
      fetchLead();
    } catch (err) {
      window.toast.error("Failed");
    }
  }, [sendRequest, leadId, fetchLead]);

  const handleDelete = useCallback(async () => {
    try {
      await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/admin/lead-hunter/leads/${leadId}`, "DELETE");
      window.toast.success("Lead deleted");
      onBack();
    } catch (err) {
      window.toast.error("Delete failed");
    }
    setShowDeleteModal(false);
  }, [sendRequest, leadId, onBack]);

  const handleMarkContacted = useCallback(async () => {
    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/admin/lead-hunter/leads/${leadId}/mark-contacted`,
        "POST",
        JSON.stringify({ notes: "[Manual] Contacted via form/phone/LinkedIn" }),
      );
      window.toast.success("Marked as contacted — no follow-ups will be sent");
      fetchLead();
    } catch (err) {
      window.toast.error("Failed");
    }
  }, [sendRequest, leadId, fetchLead]);

  if (!lead) return <p>Loading...</p>;

  const crawl = lead.crawlData || {};
  const scoring = lead.scoring || {};
  const canApprove = ["NEW", "CRAWLED", "CRAWL_FAILED", "SCORED", "AUTO_REJECTED"].includes(lead.status);
  const hasOutreach = !!outreach;

  return (
    <div>
      <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "12px" }}>
        <Button onClick={onBack} size="small" inverse>Back</Button>
        <h3 style={{ margin: 0 }}>{lead.companyName}</h3>
        <Chip label={lead.status} size="small" />
        <div style={{ marginLeft: "auto" }}>
          <Button onClick={() => setShowDeleteModal(true)} size="small" danger>Delete</Button>
        </div>
      </div>

      <Grid container spacing={2}>
        {/* Left: Company Info */}
        <Grid item xs={12} md={6}>
          <div style={sectionStyle}>
            <h4 style={{ marginTop: 0 }}>Company Info</h4>
            <div style={{ marginBottom: "8px" }}>
              <label style={{ fontSize: "13px", color: "#666" }}>Company Name</label>
              <input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                style={{ width: "100%", padding: "6px", borderRadius: "4px", border: "1px solid #ccc" }}
              />
            </div>
            <p style={{ margin: "4px 0" }}>
              <strong>Website:</strong>{" "}
              {lead.website && !/^(usdot|places|manual):/i.test(lead.website) ? (
                <a href={`https://${lead.website}`} target="_blank" rel="noopener noreferrer">{lead.website}</a>
              ) : (
                <span style={{ color: "#999" }}>{lead.website || "none yet"}</span>
              )}
            </p>
            <p style={{ margin: "4px 0" }}><strong>Source:</strong> {lead.source}</p>
            <p style={{ margin: "4px 0" }}><strong>Location:</strong> {[lead.city, lead.state || lead.region, lead.country].filter(Boolean).join(", ")}</p>
            {lead.usdot && <p style={{ margin: "4px 0" }}><strong>USDOT:</strong> {lead.usdot}</p>}
            {lead.legalName && <p style={{ margin: "4px 0" }}><strong>Legal name:</strong> {lead.legalName}</p>}
            {lead.driversReported != null && <p style={{ margin: "4px 0" }}><strong>Drivers:</strong> {lead.driversReported}</p>}
            {lead.icpTier && <p style={{ margin: "4px 0" }}><strong>ICP tier:</strong> {lead.icpTier}</p>}
            <p style={{ margin: "4px 0" }}><strong>Discovered:</strong> {new Date(lead.discoveredAt).toLocaleDateString()}</p>
            {crawl.transportType && <p style={{ margin: "4px 0" }}><strong>Transport Type:</strong> {crawl.transportType}</p>}
            {crawl.fleetSizeGuess && <p style={{ margin: "4px 0" }}><strong>Fleet Size:</strong> {crawl.fleetSizeGuess}</p>}
            {crawl.companySizeEstimate && <p style={{ margin: "4px 0" }}><strong>Company Size:</strong> {crawl.companySizeEstimate}</p>}
            {crawl.competitorUsage && <p style={{ margin: "4px 0", color: "#d32f2f" }}><strong>Competitor:</strong> {crawl.competitorUsage}</p>}

            <div style={{ marginTop: "12px" }}>
              <label style={{ fontSize: "13px", color: "#666" }}>Phone</label>
              <input
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                style={{ width: "100%", padding: "6px", borderRadius: "4px", border: "1px solid #ccc" }}
              />
            </div>

            <div style={{ marginTop: "12px" }}>
              <label style={{ fontSize: "13px", color: "#666" }}>Status</label>
              <select
                value={lead.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                style={{ width: "100%", padding: "6px", borderRadius: "4px", border: "1px solid #ccc" }}
              >
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div style={{ marginTop: "12px" }}>
              <label style={{ fontSize: "13px", color: "#666" }}>Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                style={{ width: "100%", padding: "6px", borderRadius: "4px", border: "1px solid #ccc" }}
              >
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c} value={c}>{c.replace(/_/g, " ")}</option>
                ))}
              </select>
            </div>

            <div style={{ marginTop: "12px" }}>
              <label style={{ fontSize: "13px", color: "#666" }}>Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                style={{ width: "100%", padding: "6px", borderRadius: "4px", border: "1px solid #ccc", resize: "vertical" }}
              />
            </div>
            <div style={{ marginTop: "8px", display: "flex", gap: "8px" }}>
              <Button onClick={handleSave} size="small">Save</Button>
              <Button onClick={handleRecrawl} size="small" inverse>Recrawl</Button>
            </div>
          </div>

          {/* Crawl Data */}
          <div style={sectionStyle}>
            <h4 style={{ marginTop: 0 }}>Crawl Data</h4>
            {crawl.crawledAt ? (
              <>
                <p style={{ margin: "4px 0" }}><strong>Page Title:</strong> {crawl.title || "-"}</p>
                <p style={{ margin: "4px 0", fontSize: "13px", color: "#666" }}>{crawl.metaDescription || ""}</p>
                <p style={{ margin: "4px 0" }}><strong>Pages Crawled:</strong> {crawl.pagesCrawled || 0}</p>
                <p style={{ margin: "4px 0" }}><strong>Crawled:</strong> {new Date(crawl.crawledAt).toLocaleDateString()}</p>

                <div style={{ marginTop: "8px" }}>
                  <label style={{ fontSize: "13px", color: "#666" }}>Emails (comma-separated)</label>
                  <input
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="info@company.com, sales@company.com"
                    style={{ width: "100%", padding: "6px", borderRadius: "4px", border: "1px solid #ccc", fontSize: "13px" }}
                  />
                </div>
                {crawl.hasForm && (
                  <div style={{ marginTop: "8px" }}>
                    <strong>Contact Form:</strong>{" "}
                    {crawl.contactFormUrl ? (
                      <a href={crawl.contactFormUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2" }}>
                        {crawl.contactFormUrl.length > 60 ? crawl.contactFormUrl.slice(0, 60) + "..." : crawl.contactFormUrl}
                      </a>
                    ) : (
                      <span style={{ color: "#ed6c02" }}>Form detected (no direct URL)</span>
                    )}
                  </div>
                )}
                <div style={{ marginTop: "8px" }}>
                  <strong>Keywords:</strong>{" "}
                  {(crawl.keywords || []).map((k) => <Chip key={k} label={k} size="small" variant="outlined" style={{ margin: "2px" }} />)}
                </div>
              </>
            ) : lead.usdot || lead.driversReported || lead.phone ? (
              <>
                <p style={{ margin: "4px 0", color: "#666" }}>FMCSA census — no company website crawled yet</p>
                {lead.usdot ? <p style={{ margin: "4px 0" }}><strong>USDOT:</strong> {lead.usdot}</p> : null}
                {lead.driversReported ? <p style={{ margin: "4px 0" }}><strong>Drivers:</strong> {lead.driversReported}</p> : null}
                {lead.powerUnits ? <p style={{ margin: "4px 0" }}><strong>Power units:</strong> {lead.powerUnits}</p> : null}
                {lead.phone ? <p style={{ margin: "4px 0" }}><strong>Phone:</strong> {lead.phone}</p> : null}
                <div style={{ marginTop: "8px" }}>
                  <label style={{ fontSize: "13px", color: "#666" }}>Emails (comma-separated)</label>
                  <input
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="info@company.com, sales@company.com"
                    style={{ width: "100%", padding: "6px", borderRadius: "4px", border: "1px solid #ccc", fontSize: "13px" }}
                  />
                </div>
              </>
            ) : (
              <p style={{ color: "#999" }}>Not crawled yet</p>
            )}
          </div>
        </Grid>

        {/* Right: Score + Outreach */}
        <Grid item xs={12} md={6}>
          {/* Score */}
          <div style={sectionStyle}>
            <h4 style={{ marginTop: 0 }}>Score</h4>
            {scoring.score !== null && scoring.score !== undefined ? (
              <>
                <div style={{ textAlign: "center", marginBottom: "12px" }}>
                  <span style={{ fontSize: "48px", fontWeight: "bold", color: SCORE_COLOR(scoring.score) }}>
                    {scoring.score}
                  </span>
                  <span style={{ fontSize: "20px", color: "#999" }}>/100</span>
                </div>
                <p style={{ textAlign: "center", margin: "4px 0" }}>
                  <Chip label={scoring.category?.replace(/_/g, " ") || "OTHER"} />
                </p>
                {(scoring.signals || []).length > 0 && (
                  <div style={{ marginTop: "12px" }}>
                    <strong>Signals:</strong>
                    <ul style={{ margin: "4px 0", paddingLeft: "20px", fontSize: "13px" }}>
                      {scoring.signals.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
                {(scoring.negativeHits || []).length > 0 && (
                  <div style={{ marginTop: "8px" }}>
                    <strong style={{ color: "#d32f2f" }}>Negative:</strong>{" "}
                    {scoring.negativeHits.map((n) => <Chip key={n} label={n} size="small" color="error" variant="outlined" style={{ margin: "2px" }} />)}
                  </div>
                )}
              </>
            ) : (
              <p style={{ color: "#999" }}>Not scored yet</p>
            )}
          </div>

          {/* Actions */}
          <div style={{ ...sectionStyle, display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {canApprove && (
              <Button onClick={() => setShowApproveModal(true)} size="small" disabled={approving}>
                {approving ? "Generating..." : "Approve & Generate Draft"}
              </Button>
            )}
            {canApprove && (
              <Button onClick={() => setShowRejectModal(true)} size="small" danger>Reject</Button>
            )}
            {lead.status === "OUTREACH" && (
              <>
                <Button onClick={handleMarkReplied} size="small">Mark Replied</Button>
                <Button onClick={handleMarkSold} size="small">Mark Sold</Button>
              </>
            )}
            {["SCORED", "APPROVED", "OUTREACH"].includes(lead.status) && (
              <Button onClick={handleMarkContacted} size="small" inverse>Mark Contacted</Button>
            )}
          </div>

          {/* Outreach */}
          {hasOutreach && (
            <>
              <OutreachTimeline outreach={outreach} />
              <DraftEditor outreach={outreach} onUpdate={fetchLead} />
            </>
          )}
        </Grid>
      </Grid>

      <AreYouSureModal
        showModal={showApproveModal}
        title="Approve Lead"
        text="This will generate a draft email using AI. Continue?"
        onClose={() => setShowApproveModal(false)}
        onConfirmed={handleApprove}
      />
      <AreYouSureModal
        showModal={showRejectModal}
        title="Reject Lead"
        text="This lead will be marked as rejected. Continue?"
        onClose={() => setShowRejectModal(false)}
        onConfirmed={handleReject}
      />
      <AreYouSureModal
        showModal={showDeleteModal}
        title="Delete Lead"
        text="This will permanently delete this lead and all its outreach data. This cannot be undone."
        onClose={() => setShowDeleteModal(false)}
        onConfirmed={handleDelete}
      />
    </div>
  );
};

export default LeadDetail;
